import cn from "classnames";
import { format } from "date-fns";
import { useDebounce } from "frontend/hooks/debounce";
import useDebounceCallback from "frontend/hooks/use-debounce-callback";
import type { SyncService } from "frontend/services/syncService";
import LoadedState from "frontend/state/loadedState";
import { useCanvasElementById, useItemsFromReplicache } from "frontend/subscriptions";
import tracking from "frontend/tracking";
import DatePicker from "frontend/ui-components/date-picker";
import { Dialog, Tooltip } from "frontend/ui-components/floaters/floaters";
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "frontend/ui-components/floaters/popover";
import { pencilIcon } from "frontend/ui-components/svg-shapes";
import { useAtomValue } from "jotai";
import React, { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";
import consts from "shared/consts";
import { createTag } from "shared/datamodel/metadata";
import type { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { Users } from "shared/datamodel/schemas/app-state";
import { Tag, TagType } from "shared/datamodel/schemas/metadata";
import type { TaskCard } from "shared/datamodel/schemas/task-card";
import { canvasMetadataPrefix, dbKey } from "shared/util/utils";
import { boardAtom, userAtom, usersAtom, usersStateAtom } from "state-atoms/users-atoms";
import { mixColors } from "utils/color-utils";
import { getPathPrefix } from "../../utils/getPathPrefix";
import { handleTabInTextArea } from "../text-element";
import styles from "./task-card-open-element.module.css";

type MapValueType<A> = A extends Map<any, infer V> ? V : never;

const TrashIcon = () => (
  <svg
    className={styles.icon}
    width="18"
    height="20"
    viewBox="0 0 18 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 5H17 M7 9V15 M11 9V15 M2 5.5L3.5 19H5H13H14.5L16 5.5 M12 5V1H6V5"
      strokeWidth={1.4}
      strokeLinecap="square"
    />
  </svg>
);

const ShareIcon = () => (
  <svg
    className={styles.icon}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 11a3.483 3.483 0 0 0 5 0l4-4a3.5 3.5 0 1 0-5-5l-.5.5 M11 7a3.485 3.485 0 0 0-5 0L2 11a3.5 3.5 0 0 0 5 5l.5-.5"
      strokeWidth={1.4}
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

// functions to convert between long and short ids:
// cMetadata-Tag-XXXXXX <-> XXXXXX
function getShortId(elementId: string) {
  if (elementId.startsWith(canvasMetadataPrefix)) {
    return elementId.substring(canvasMetadataPrefix.length + TagType.length + 1);
  }
  return elementId;
}
function getLongId(elementId: string) {
  if (elementId.startsWith(canvasMetadataPrefix)) {
    return elementId;
  }
  return canvasMetadataPrefix + TagType + "-" + elementId;
}

const ELEMENT_FROM_REFLECT_DEFAULT_VALUE = {
  color: "white",
  dueDate: 0,
  title: "",
  tags: [],
  assignees: [],
};

export default function TaskCardOpen({
  id,
  syncService,
  element: elementData,
  onDismiss,
  onDeleteElement,
  onChangeElement,
}: {
  id: string;
  syncService: SyncService<RW> | null;
  element?: TaskCard;
  onDismiss: () => void;
  onDeleteElement: () => void;
  onChangeElement: (
    props: any,
    undoConfig: { shouldAdd: boolean; previousProps?: any },
    updateSubMenuData?: any
  ) => void;
}) {
  const user = useAtomValue(userAtom);
  const users = useAtomValue(usersAtom);
  const usersState = useAtomValue(usersStateAtom);
  const board = useAtomValue(boardAtom);

  const allTags = useItemsFromReplicache(syncService?.getReplicache(), canvasMetadataPrefix + TagType, []);

  const elementFromReflect = useCanvasElementById(syncService?.getReplicache(), id, ELEMENT_FROM_REFLECT_DEFAULT_VALUE);

  const element: TaskCard = elementData ?? elementFromReflect;

  const isInsideOtherElement = element && !!element.containerId?.includes(consts.CANVAS_ELEMENTS.GANTT);

  const reportEventChanged = useDebounceCallback((field: string) => {
    const eventName = isInsideOtherElement ? "gantt_task_edited" : "task_edited";
    tracking.trackAnalyticsEvent(eventName, {
      from: "task-popup",
      field: field,
    });
  }, 200);

  const {
    color = "black",
    dueDate = 0,
    fromDate: fromDateNumber = dueDate,
    toDate: toDateNumber = fromDateNumber,
    tags: cardTags = [],
  } = element;
  const userPartOfBoardAccount = user?.account?.id && board?.accountId && user?.account?.id == board?.accountId;

  const dispatch = ({ type, payload }: { type: string; payload?: any }) => {
    if (!payload?.id) {
      console.error("tag operation with empty tag id", payload.id);
      return;
    }
    switch (type) {
      case "toggle-user-tag": {
        let tags = cardTags;
        const tag = payload.id;
        if (tags.includes(tag)) tags = tags.filter((item) => item != tag);
        else tags = [...tags, tag];
        onChangeElement({ tags: tags }, { shouldAdd: false });
        break;
      }
      case "create-new-tag": {
        const id = dbKey(payload.id, TagType);
        syncService?.mutate.putMetadata({
          id: id,
          value: payload.value,
        });
        break;
      }
      case "delete-tag": {
        const id = dbKey(payload.id, TagType);
        syncService?.mutate.deleteMetadata({ id: id });
        break;
      }
      case "modify-tag": {
        const id = dbKey(payload.id, TagType);
        syncService?.mutate.changeMetadata({
          id: id,
          value: payload.value,
        });
        break;
      }
    }
  };

  const descriptionInputRef = useRef<any>();
  const titleRef = useRef<any>();
  const [title, setTitle] = useState(element.title);
  const [showCopiedLink, setShowCopiedLink] = useState<boolean>(false);
  const hasAssignee = element.assignees && element.assignees.length > 0;

  const initialTitle = useRef(element.title);
  useEffect(() => {
    if (initialTitle.current !== element.title) {
      setTitle(element.title);
      initialTitle.current = element.title;
    }
  }, [element.title]);

  function copyLink() {
    applyCardUpdates();
    const me = window.location;
    navigator.clipboard.writeText(me.origin + me.pathname + "#focus=" + id);
    //setFocusedElement(id);
    setShowCopiedLink(true);
    setTimeout(() => setShowCopiedLink(false), 2000);
  }

  function applyCardUpdates() {
    onChangeElement(
      {
        title,
        description: descriptionInputRef.current.value,
      },
      { shouldAdd: false }
    );
  }

  function onClose() {
    applyCardUpdates();
    onDismiss();
  }

  useLayoutEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = "auto";
      titleRef.current.style.height = titleRef.current.scrollHeight + "px";
    }
  }, [titleRef]);
  // ----------------------------------------------------------------

  function renderCardTags(allTags: [string, any][], cardTags: string[]) {
    return (
      <div className={styles.tagsArea}>
        {cardTags.map((shortId) => {
          const fullid = getLongId(shortId);
          const tag = allTags.find(([id]) => id == fullid);
          return tag && renderTagElement(tag[0], tag[1], true);
        })}
      </div>
    );
  }

  function renderTagElement(id: string, tag: Tag, selected: boolean, onClick?: React.MouseEventHandler<HTMLElement>) {
    return (
      <button
        key={id}
        id={id}
        style={{
          backgroundColor: selected ? tag.color : "white",
          border: selected ? "1px transparent solid" : "1px " + tag.color + " solid",
          color: selected ? "white" : tag.color,
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={onClick}
        className={styles.tag}
      >
        <div>{tag.name}</div>
      </button>
    );
  }

  // ----------------------------------------------------------------

  function renderDateContainer() {
    const fromDate = dateFromNumber(fromDateNumber);
    const toDate = dateFromNumber(toDateNumber);
    if (!fromDate || !toDate) {
      return null;
    }

    const isRange = fromDate.getTime() !== toDate.getTime();
    const dateLabel = !isRange && "Due date:";
    const dateValue = isRange
      ? `${format(fromDate, "MMM dd, yyyy")} - ${format(toDate, "MMM dd, yyyy")}`
      : format(fromDate, "MMM dd, yyyy");
    return (
      <>
        {dateLabel && <span className={styles.dueDateLabel}>{dateLabel}</span>}
        <span className={styles.dueDateText}>
          <img src="/images/due-date-icon.svg" width={18} />
          {dateValue}
        </span>
      </>
    );
  }

  return (
    <Dialog onClose={onClose}>
      <div
        className={styles.taskCardDialog}
        style={
          {
            "--task-color": color,
            "--task-lighter-color": mixColors(color, "#ffffff", 0.3),
          } as CSSProperties
        }
      >
        <div className={styles.topbar} />
        <div className={styles.main}>
          {renderCardTags(allTags, cardTags)}
          <textarea
            tabIndex={1}
            ref={titleRef}
            className={styles.title}
            placeholder="Title..."
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
              reportEventChanged("title");
            }}
            onKeyDown={handleTabInTextArea}
          />
          <textarea
            tabIndex={2}
            ref={descriptionInputRef}
            className={styles.description}
            placeholder="Description"
            defaultValue={element.description}
            onKeyDown={handleTabInTextArea}
            onChange={() => {
              reportEventChanged("description");
            }}
          />
          <div className={styles.statusLine}>
            {hasAssignee && (
              <>
                <span className={styles.assigneeLabel}>Assigned to:</span>
                {userPartOfBoardAccount ? (
                  <>
                    <span className={styles.assigneeText}> {element.assignees![0].name} </span>
                    <img
                      className={styles.circular}
                      width={20}
                      height={20}
                      src={element.assignees![0].thumbnail}
                      alt={element.assignees![0].name}
                    />
                  </>
                ) : (
                  <img
                    className={styles.circular}
                    width={20}
                    height={20}
                    src={getPathPrefix("/images/my-profile-icon.svg")}
                    style={{ background: "#113255" }}
                  />
                )}
              </>
            )}
            {hasAssignee && !!dueDate && <span className={styles.statusLineSpacer} />}
            {dueDate != 0 && (
              <>
                <span className={styles.dueDateLabel}>Due date:</span>
                <span className={styles.dueDateText}>
                  <img src={getPathPrefix("/images/due-date-icon.svg")} width={18} />
                  {format(dueDate, "yyyy MMM d")}
                </span>
              </>
            )}
            {dueDate === 0 && renderDateContainer()}
          </div>
        </div>

        <div className={styles.right}>
          <div className={styles.rightPaneButtons}>
            {!isInsideOtherElement && (
              <ColorSelector
                onColorSelected={(color) => {
                  onChangeElement({ color }, { shouldAdd: false });
                  reportEventChanged("color");
                }}
              >
                <div className={styles.menuButton}>
                  <ColorCircle color={color} size={15} />
                  Task color
                  <span className={styles.gap} />
                  <DropdownChevron />
                </div>
              </ColorSelector>
            )}
            <Tags allTags={allTags} cardTags={cardTags} dispatch={dispatch} />
            {board?.accountId !== "9743496" && (
              <Assignee
                users={users}
                usersState={usersState}
                assignees={element.assignees}
                onSelected={(selectedUser) => {
                  // this totally assumes a single assignee
                  if (selectedUser) {
                    onChangeElement(
                      {
                        assignees: [
                          {
                            id: selectedUser.id,
                            name: selectedUser.name,
                            email: selectedUser.email,
                            thumbnail: selectedUser.thumbnail ?? selectedUser.photo,
                          },
                        ],
                      },
                      { shouldAdd: false }
                    );
                  } else {
                    onChangeElement({ assignees: [] }, { shouldAdd: false });
                  }
                }}
                disabled={!userPartOfBoardAccount}
              />
            )}
            <CardDatePicker
              from={fromDateNumber}
              to={toDateNumber}
              onChange={(fromDate, toDate) => {
                onChangeElement({ fromDate, toDate }, { shouldAdd: false });
                reportEventChanged("date");
              }}
            />

            <div className={styles.horizontalButtonsSmall}>
              <span className={styles.notification} hidden={!showCopiedLink}>
                Link copied!
              </span>
              <Tooltip label={"Get link"} tooltipClassName={styles.tooltip}>
                <button className={styles.resetButton} disabled={showCopiedLink} tabIndex={7} onClick={copyLink}>
                  <ShareIcon />
                </button>
              </Tooltip>

              <Tooltip label={"Delete card"} tooltipClassName={styles.tooltip}>
                <button className={styles.resetButton} tabIndex={8} onClick={onDeleteElement}>
                  <TrashIcon />
                </button>
              </Tooltip>
            </div>
          </div>
          <button tabIndex={9} className={cn(styles.closeButton, styles.resetButton)} onClick={onClose}>
            <img src={getPathPrefix("/images/x.svg")} alt="Close" />
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function ColorSelector({
  onColorSelected,
  palette = consts.COLOR_PALETTE,
  children,
}: {
  onColorSelected: (color: string) => void;
  palette?: string[];
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  function selectColor(e: React.MouseEvent<Element>) {
    let node;
    for (node = e.target as HTMLElement; node && !node.id; ) node = node.parentNode as HTMLElement;
    const newColor = node?.id;
    onColorSelected(newColor);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen} offsetFromTrigger={15}>
      <PopoverTrigger asChild>
        <div onClick={() => setOpen((v) => !v)}>{children}</div>
      </PopoverTrigger>
      <PopoverContent>
        <div className={cn(styles.floater, styles.colorPicker)}>
          {palette.map((color) => (
            <div className={styles.colorCircleCell} key={color} id={color} onClick={selectColor}>
              <ColorCircle color={color} size={22} />
            </div>
          ))}
        </div>
        <PopoverArrow fill="white" />
      </PopoverContent>
    </Popover>
  );
}

function Tags({ allTags, cardTags, dispatch }: { allTags: [string, Tag][]; cardTags: string[]; dispatch: any }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} offsetFromTrigger={15}>
      <PopoverTrigger className={styles.resetButton} onClick={() => setOpen((v) => !v)}>
        <div className={styles.menuButton}>
          <img src={getPathPrefix("/images/tags-icon.svg")} />
          Tags
          <span className={styles.gap} />
          <DropdownChevron />
        </div>
      </PopoverTrigger>
      <PopoverContent>
        <TagsWindow tags={allTags} cardTags={cardTags} dispatch={dispatch} />
        <PopoverArrow fill="white" />
      </PopoverContent>
    </Popover>
  );
}

//TODO: might be unified with TaskColor
// needs color-palette as prop, value, onChange in props
function TagColorPicker({ newColor, setNewColor }: { newColor: string; setNewColor: (color: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} offsetFromTrigger={15}>
      <PopoverTrigger asChild={true}>
        <div onClick={() => setOpen((v) => !v)}>
          <ColorCircle color={newColor} size={18} />
        </div>
      </PopoverTrigger>
      <PopoverContent>
        <div className={cn(styles.floater, styles.colorPicker)}>
          {consts.COLOR_PALETTE.map((color) => (
            <div
              className={styles.colorCircleCell}
              key={color}
              id={color}
              onClick={(e: any) => {
                let node;
                for (node = e.target as HTMLElement; node && !node.id; ) node = node.parentNode as HTMLElement;
                const newColor = node?.id;
                setNewColor(newColor);
                setOpen(false);
              }}
            >
              <ColorCircle color={color} />
            </div>
          ))}
        </div>
        <PopoverArrow fill="white" />
      </PopoverContent>
    </Popover>
  );
}

function Assignee({
  users,
  usersState,
  assignees,
  onSelected,
  disabled,
}: {
  users: Users;
  usersState: LoadedState;
  assignees?: { id: number }[];
  onSelected: (selectedUser: any) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen} offsetFromTrigger={15}>
      <PopoverTrigger className={styles.resetButton} onClick={() => setOpen((v) => !v)}>
        <div className={styles.menuButton}>
          <img src={getPathPrefix("/images/assignee-icon.svg")} />
          Assignee
          <span className={styles.gap} />
          <DropdownChevron />
        </div>
      </PopoverTrigger>
      <PopoverContent>
        <PeoplePicker
          users={users}
          usersState={usersState}
          assignees={assignees}
          onSelected={(e) => {
            setOpen(false);
            onSelected(e);
          }}
        />
        <PopoverArrow fill="white" />
      </PopoverContent>
    </Popover>
  );
}

function dateFromNumber(value: number): Date | undefined {
  if (!Number.isFinite(value)) {
    return undefined;
  }
  try {
    return value != 0 ? new Date(value) : undefined;
  } catch {
    return undefined;
  }
}

function CardDatePicker({
  from,
  to,
  onChange,
}: {
  from: number;
  to: number;
  onChange: (from: number, to: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const fromDate = dateFromNumber(from);
  const toDate = dateFromNumber(to);

  return (
    <Popover open={open} onOpenChange={setOpen} offsetFromTrigger={15}>
      <PopoverTrigger className={styles.resetButton} onClick={() => setOpen((v) => !v)}>
        <div className={styles.menuButton}>
          <img src={getPathPrefix("/images/due-date-icon.svg")} width={15} height={15} />
          Due Date
          <span className={styles.gap} />
          <DropdownChevron />
        </div>
      </PopoverTrigger>
      <PopoverContent>
        <div className={styles.floater}>
          <DatePicker
            fromDate={fromDate}
            toDate={toDate}
            enableRangeSelection
            onSelected={(from: Date, to?: Date) => {
              const fromDate = from.valueOf();
              const toDate = to?.valueOf() ?? fromDate;
              onChange(fromDate, toDate);
            }}
          />
          <div className={styles.footer}>
            <button
              className={styles.resetButton}
              disabled={fromDate === undefined}
              onClick={() => {
                onChange(0, 0);
                setOpen(false);
              }}
            >
              Clear
            </button>
          </div>
        </div>
        <PopoverArrow fill="white" />
      </PopoverContent>
    </Popover>
  );
}

function ColorCircle({ color, size }: { color: string; size?: number }) {
  return (
    <div
      style={{
        backgroundColor: color,
        aspectRatio: "1",
        borderRadius: "50%",
        height: size,
        width: size,
        verticalAlign: "middle",
      }}
    />
  );
}

function TagsWindow({ tags, cardTags, dispatch }: { tags: [string, Tag][]; cardTags: string[]; dispatch: any }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [newColor, setNewColor] = useState(consts.COLOR_PALETTE[0]);
  const [newName, setNewName] = useState("");

  function closeAndApply() {
    setIsEditMode(false);
  }

  const lowerCaseInputText = newName.trim().toLocaleLowerCase();
  const canCreateNewTag = lowerCaseInputText != "" && !tags.find(([, value]) => value.name == newName);

  const tagsFiltered = tags.filter(([, value]) => value.name.toLocaleLowerCase().includes(lowerCaseInputText));
  function cmp(a: Tag, b: Tag) {
    const aKey = a.order ?? a.lastModifiedTimestamp;
    const bKey = b.order ?? b.lastModifiedTimestamp;
    return aKey! - bKey!;
  }
  tagsFiltered.sort((a, b) => cmp(a[1], b[1]));
  return (
    <div className={styles.floatingPopup} style={{ width: 300 }}>
      <form
        className={styles.inputLine}
        onSubmit={(e) => {
          dispatch({
            type: "create-new-tag",
            payload: createTag(newName, newColor),
          });
          setNewName("");
          e.preventDefault();
        }}
      >
        <TagColorPicker newColor={newColor} setNewColor={setNewColor} />
        <input
          type="text"
          placeholder="Type a tag name..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <input type="Submit" value="Add tag" readOnly disabled={!canCreateNewTag} hidden={!canCreateNewTag} />
      </form>
      {isEditMode && tagsFiltered.length > 0 ? (
        <EditableTagsWindow tags={tagsFiltered} dispatch={dispatch} closeAndApply={closeAndApply} />
      ) : (
        <>
          <div className={styles.tagsFlexiLine}>
            {tagsFiltered.map(([fullid, tag]) =>
              renderTagElement(
                fullid,
                tag,
                cardTags.some((x) => fullid.includes(x)),
                (e: React.MouseEvent<HTMLElement>) => {
                  const tagId = getShortId((e.currentTarget as HTMLElement).id);
                  dispatch({ type: "toggle-user-tag", payload: { id: tagId } });
                }
              )
            )}
          </div>
          {!!tagsFiltered.length && (
            <>
              <div className={styles.hr} />
              <button className={cn(styles.tagsAreaButton, styles.resetButton)} onClick={() => setIsEditMode(true)}>
                {pencilIcon}
                Edit Tags
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

function EditableTagsWindow({
  tags,
  dispatch,
  closeAndApply,
}: {
  tags: [string, any][];
  dispatch: any;
  closeAndApply: () => void;
}) {
  return (
    <>
      <div className={styles.tagsEditArea}>
        {tags.map(([longId, tag]) => {
          return (
            <div key={longId} className={styles.tagInEditMode}>
              <ColorSelector
                onColorSelected={(color) => {
                  dispatch({
                    type: "modify-tag",
                    payload: {
                      id: getShortId(longId),
                      value: {
                        type: tag.type,
                        name: tag.name,
                        color,
                        order: tag.order ?? tag.lastModifiedTimestamp,
                      } as Tag,
                    },
                  });
                }}
              >
                <ColorCircle color={tag.color} size={15} />
              </ColorSelector>
              <input
                type="text"
                value={tag.name}
                onChange={(e) => {
                  dispatch({
                    type: "modify-tag",
                    payload: {
                      id: getShortId(longId),
                      value: {
                        type: tag.type,
                        name: e.target.value,
                        color: tag.color,
                        order: tag.order ?? tag.lastModifiedTimestamp,
                      } as Tag,
                    },
                  });
                }}
              />
              <img
                src={getPathPrefix("/images/x.svg")}
                onClick={() => {
                  dispatch({ type: "delete-tag", payload: { id: getShortId(longId) } });
                  // if we just deleted the last tag, close the window
                  if (tags.length == 1) {
                    closeAndApply();
                  }
                }}
              />
            </div>
          );
        })}
      </div>
      <div className={styles.hr} />
      <button className={cn(styles.tagsAreaButton, styles.resetButton)} onClick={closeAndApply}>
        Close
      </button>
    </>
  );
}

function renderTagElement(id: string, tag: Tag, selected: boolean, onClick?: React.MouseEventHandler<HTMLElement>) {
  return (
    <button
      key={id}
      id={id}
      style={{
        backgroundColor: selected ? tag.color : "white",
        border: selected ? "1px transparent solid" : "1px " + tag.color + " solid",
        color: selected ? "white" : tag.color,
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
      className={styles.tag}
    >
      <div>{tag.name}</div>
    </button>
  );
}

function PeoplePicker({
  users,
  usersState,
  assignees,
  onSelected,
}: {
  users: Users;
  usersState: LoadedState;
  assignees?: { id: number }[];
  onSelected: (selectedUser: any) => void;
}) {
  const [assigneeSearchInput, setAssigneeSearchInput] = useState("");
  const searchTerm = useDebounce(assigneeSearchInput, 500);

  const usersIdAsArray = [...users.keys()];
  const hasAssignee = assignees && assignees.length > 0;

  const nonAssigned = !hasAssignee
    ? usersIdAsArray
    : usersIdAsArray.filter((id) => !assignees.find((person) => person.id == id));

  const filteredBySearchTerm = nonAssigned.filter((id) => users.get(id)!.name.toLocaleLowerCase().includes(searchTerm));

  const listLength = filteredBySearchTerm.length;
  const itemHeight = 50;
  const listHeight = Math.min(listLength * itemHeight, 400);
  function getUser(id: any) {
    return users.get(Number(id));
  }
  return (
    <div className={styles.peopleList}>
      <input
        type="text"
        placeholder="Search people"
        value={assigneeSearchInput}
        onChange={(e) => setAssigneeSearchInput(e.target.value)}
      />
      {hasAssignee && (
        <>
          <div className={styles.usersList}>
            {assignees!.map((user) => (
              <div
                key={user.id}
                data-id={user.id.toString()}
                onClick={() => {
                  setAssigneeSearchInput("");
                  // this assumes there's only a single assignee !
                  onSelected(null);
                }}
              >
                <Person user={getUser(user.id)!} selected={true} />
              </div>
            ))}
          </div>
          <div className={styles.hr} style={{ marginTop: 10, marginBottom: 10 }} />
        </>
      )}
      {usersState == LoadedState.loading ? (
        <h1>&quot;Loading...&quot;</h1>
      ) : usersState == LoadedState.failed ? (
        <h2>&quot;Failed to load members list.&quot;</h2>
      ) : usersState == LoadedState.finished ? (
        <List
          height={listHeight}
          width={300}
          itemCount={listLength}
          itemSize={itemHeight}
          itemKey={(index: number) => filteredBySearchTerm[index]}
          className={styles.usersList}
        >
          {({ index, style }: { index: number; style: any }) => (
            <li
              tabIndex={0}
              style={style}
              key={filteredBySearchTerm[index]}
              data-id={filteredBySearchTerm[index]}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                const id = e.currentTarget.dataset.id;
                if (!id) return;
                const userId = parseInt(id);
                const selectedUser = users.get(userId);
                if (!selectedUser) {
                  console.log("can't find it");
                  return;
                }
                onSelected(selectedUser);
                setAssigneeSearchInput("");
              }}
            >
              <Person user={users.get(filteredBySearchTerm[index])!} />
            </li>
          )}
        </List>
      ) : null}
    </div>
  );
}

function Person({ user, selected }: { user: MapValueType<Users>; selected?: boolean }) {
  const { name, email, thumbnail } = user;
  return (
    <div className={styles.userContainer}>
      <img width={40} height={40} className={styles.userPhoto} src={thumbnail} alt={name} />
      <span className={styles.userName}>{name}</span>
      <span className={styles.userEmail}>{email}</span>
      {selected && <span className={styles.selected} />}
    </div>
  );
}

function DropdownChevron() {
  return (
    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 8.1L10 11.1L13 8.1L14 9.2L10 13.2L5.9 9.1L7 8.1Z" fill="#657E9A" />
    </svg>
  );
}
